'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Button, Dropdown, Text, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';
import { ChevronDown } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import RangeToggleGroup from '@/src/components/RangeToggleGroup';
import { rangeToggleGroupFluidStyles } from '@/src/components/RangeToggleGroup/rangeToggleGroup.styles';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { FILTER_DROPDOWN_MIN_WIDTH, FILTER_TRIGGER_LABEL_MIN_WIDTH } from './listingSearchConstants';

// Selecting '4' as maxValue means open-ended (4+ beds); anything beyond maps to null in FilterState
const BEDS_OPEN_MAX = '4';

type RoomsDropdownProps = {
  bedrooms: FilterState['bedrooms'];
  bathrooms: FilterState['bathrooms'];
  onBedroomsChange: (next: FilterState['bedrooms']) => void;
  onBathroomsChange: (next: FilterState['bathrooms']) => void;
  onReset: () => void;
  size?: 'sm' | 'md' | 'lg';
};

export default function RoomsDropdown({
  bedrooms,
  bathrooms,
  onBedroomsChange,
  onBathroomsChange,
  onReset,
  size = 'lg',
}: RoomsDropdownProps) {
  const tFilters = useTranslations('filters');
  const tListing = useTranslations('listing');

  const hasBeds = bedrooms.min !== null;
  const hasBaths = bathrooms.min !== null;
  const hasActive = hasBeds || hasBaths;

  const anyLabel = tFilters('any');

  const bedsLabel = !hasBeds
    ? anyLabel
    : bedrooms.min === 0 && bedrooms.max === 0
      ? tListing('studio')
      : bedrooms.max === null
        ? `${bedrooms.min}+`
        : bedrooms.min === bedrooms.max
          ? tListing('bed', { count: bedrooms.min! })
          : `${bedrooms.min}–${bedrooms.max}`;
  const bathsTrigger = !hasBaths ? anyLabel : `${bathrooms.min}+`;
  const combinedLabel = hasActive ? `${bedsLabel} / ${bathsTrigger}` : tFilters('anyRooms');

  const bedOptions = [
    { value: 'any', label: anyLabel },
    { value: '0', label: tListing('studio') },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4+' },
  ];

  // FilterState → RangeToggleGroup: null max maps to BEDS_OPEN_MAX so the last button shows as selected
  const bedsMinVal: string | null = bedrooms.min === null ? null : String(bedrooms.min);
  const bedsMaxVal: string | null =
    bedrooms.min === null
      ? null
      : bedrooms.max === null
        ? BEDS_OPEN_MAX
        : String(Math.min(bedrooms.max, parseInt(BEDS_OPEN_MAX, 10)));

  const handleBedsChange = useCallback(
    (minVal: string | null, maxVal: string | null) => {
      if (minVal === null) {
        onBedroomsChange({ min: null, max: null });
        return;
      }
      const min = parseInt(minVal, 10);
      const max = maxVal === null || maxVal === BEDS_OPEN_MAX ? null : parseInt(maxVal, 10);
      onBedroomsChange({ min, max });
    },
    [onBedroomsChange],
  );

  const bathOptions = [
    { value: 'any', label: anyLabel },
    { value: '1', label: '1+' },
    { value: '2', label: '2+' },
    { value: '3', label: '3+' },
    { value: '4', label: '4+' },
  ];

  const bathsValue = bathrooms.min === null ? 'any' : String(bathrooms.min);

  const handleBathsChange = useCallback(
    (next: string | Array<string>) => {
      if (typeof next !== 'string') return;
      onBathroomsChange(next === 'any' ? { min: null } : { min: Number(next) });
    },
    [onBathroomsChange],
  );

  return (
    <Dropdown.Root placement="bottom-start">
      <Dropdown.Trigger>
        <Button.Root
          tone={hasActive ? 'brand' : 'neutral'}
          emphasis="bare"
          size={size}
          css={{
            // Match the Location combobox's pill radius at every size.
            borderRadius: 'pill',
            justifyContent: 'space-between',
          }}
        >
          <Button.Label
            css={{
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
            // Pins the pill so it can't reflow as `combinedLabel` changes —
            // see FILTER_TRIGGER_LABEL_MIN_WIDTH for the measurements. Sized on
            // this element specifically because `ch` resolves against its OWN
            // font: Button.Root inherits a plain regular-weight face, not this
            // Label's bold one.
            style={{ minWidth: FILTER_TRIGGER_LABEL_MIN_WIDTH }}
          >
            {combinedLabel}
          </Button.Label>
          <Button.Icon>
            <LucideIcon icon={ChevronDown} size="sm" />
          </Button.Icon>
        </Button.Root>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content style={{ minWidth: FILTER_DROPDOWN_MIN_WIDTH }}>
          <Dropdown.Body>
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
              {/* Bedrooms */}
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
                <Text asChild textStyle="body-sm-bold" color="text.subtle">
                  <p>{tFilters('bedrooms')}</p>
                </Text>
                <RangeToggleGroup
                  options={bedOptions}
                  minValue={bedsMinVal}
                  maxValue={bedsMaxVal}
                  onChange={handleBedsChange}
                  aria-label={tFilters('numberOfBedrooms')}
                  clearValue="any"
                  fluid
                />
              </Box>
              {/* Bathrooms */}
              <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
                <Text asChild textStyle="body-sm-bold" color="text.subtle">
                  <p>{tFilters('bathrooms')}</p>
                </Text>
                <ToggleButtonGroup
                  aria-label={tFilters('numberOfBathrooms')}
                  value={bathsValue}
                  onValueChange={handleBathsChange}
                  size="sm"
                  css={rangeToggleGroupFluidStyles}
                >
                  {bathOptions.map((option) => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Dropdown.Body>
          <Dropdown.Footer
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: hasActive ? 'space-between' : 'flex-end',
              gap: 'tighter',
            }}
          >
            {hasActive ? (
              <Button tone="neutral" emphasis="outlined" size="sm" onClick={onReset}>
                {tFilters('reset')}
              </Button>
            ) : null}
            <Dropdown.Close>
              <Button tone="brand" emphasis="filled" size="sm">
                {tFilters('done')}
              </Button>
            </Dropdown.Close>
          </Dropdown.Footer>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
